import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import BetTableDragon from "../../casinoComponents/betTableDragon";
import VideoBox from "../../casinoComponents/videoBox";
import MyBet from "../../casinoComponents/myBet";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import ViewMoreModal from "../../../components/ViewMoreModal";
import "../../../styles/games/dt20.css";
import DragonTigerCardsOverlay from "../../casinoComponents/videoBox/overlays/DragonTigerCardsOverlay";
import ResultDragonTiger from "../resultui/resultDragonTiger/ResultDragonTiger";
import {
  getCasinoGameDetails,
  getMatchExposure,
  getCasinoOpenBets,
} from "../../../apiservices/CasionApi";

const GAME_NAME_API = "dt20"; // API game key for getCasinoGameDetails

// Reference game-header titles per route (reference: lastresults2 res1.cname)
const TITLES = {
  dt20: "20-20 Dragon Tiger",
  pdt20: "Premium 20-20 Dragon Tiger",
  tdt20: "Tembo 20-20 Dragon Tiger",
};

function extractCasinoGame(pathname) {
  const match = pathname.match(/\/casino\/([^/?#]+)/);
  return match ? match[1] : null;
}

export default function DragonTiger() {
  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || GAME_NAME_API;

  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const subData =
    gameData?.data?.data?.sub || gameData?.data?.sub || [];

  // Game data: poll every 1.5s (same as layout doc); use gameName from route (dt20 / pdt20)
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const data = await getCasinoGameDetails(gameName);
        setGameData(data);
      } catch (err) {
        console.error("Error fetching game data:", err);
      }
    };
    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [gameName]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      const matchId =
        gameData?.data?.data?.mid?.toString() ||
        gameData?.data?.mid?.toString() ||
        gameData?.mid?.toString();

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            exposureMap[item.team_name] =
              parseFloat(item.exposure_amount) || 0;
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [
    gameData?.data?.data?.mid,
    gameData?.data?.mid,
    gameData?.mid,
  ]);

  // My Bets: getCasinoOpenBets(gameName), poll every 5s (aligned with lord-admin)
  const fetchMyBetsData = useCallback(async () => {
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet:
            bet.matchedBet ||
            bet.nation ||
            bet.selection ||
            bet.player_name ||
            "-",
          selection:
            bet.selection ||
            bet.player_name ||
            bet.matchedBet ||
            "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype:
            bet.gametype ??
            bet.game_type ??
            (bet.type === "lay"
              ? "Lay"
              : bet.type === "back"
              ? "Back"
              : "-"),
          type: bet.type || null,
          exposer:
            parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [gameName]);

  useEffect(() => {
    const matchId =
      gameData?.data?.data?.mid?.toString() ||
      gameData?.data?.mid?.toString() ||
      gameData?.mid?.toString();
    if (!matchId) return;
    const t = setTimeout(fetchExposure, 2000);
    const exposureInterval = setInterval(fetchExposure, 2000);
    return () => {
      clearTimeout(t);
      clearInterval(exposureInterval);
    };
  }, [gameData?.data?.data?.mid, gameData?.data?.mid, gameData?.mid, fetchExposure]);

  useEffect(() => {
    fetchMyBetsData();
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => clearInterval(intervalId);
  }, [fetchMyBetsData]);

  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  // Last result: from game data (lrs) per CASINO_TABLE_LAYOUT
  const resultData =
    gameData?.lrs ??
    gameData?.data?.data?.lrs ??
    gameData?.data?.lrs ??
    [];

  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;
  const feed = gameData?.data?.data ?? gameData?.data ?? {};
  const roundId = feed?.mid || gameData?.mid;
  const title = TITLES[gameName] || TITLES.dt20;

  return (
    <CasinoTableLayout
      tableClass="dt20"
      rightSide={
        <MyBet
          bets={myBets}
          onViewMore={() => setShowViewMoreModal(true)}
        />
      }
    >
      <>
        <CasinoHeading
          name={title}
          roundId={roundId || ""}
          placedBetCount={myBets.length}
          activeTab="game"
          onTabChange={() => {}}
        />

        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={feed?.lt}
          gameType={gameName}
          cardsOverlay={feed?.card != null ? <DragonTigerCardsOverlay card={feed.card} /> : null}
        />

        {/* Reference renders .casino-detail once the feed has a round (casinoData.mid) */}
        {roundId ? (
          <div className="casino-detail">
            <BetTableDragon
              data={subData}
              onBetClick={handleBetClick}
              exposures={exposures}
              myBets={myBets}
              remark={feed?.remark}
            />{" "}
            <CasinoLastResult
              gameType={gameName}
              data={resultData}
              gameData={gameData}
              viewAllLink={`/admin/reports/casinoresult/${gameName}`}
              resultComponent={<ResultDragonTiger gameType={gameName} data={resultData} />}
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
      />
    </CasinoTableLayout>
  );
}
